import { useEffect, useRef } from "react";

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
  selector?: string;
  staggerDelay?: number;
  revealClass?: string;
}

export function useScrollReveal<T extends HTMLElement>(options: UseScrollRevealOptions = {}) {
  const {
    threshold = 0.15,
    rootMargin = "0px 0px -50px 0px",
    once = true,
    selector = ".scroll-reveal",
    staggerDelay = 80,
    revealClass = "animate-reveal",
  } = options;

  const ref = useRef<T>(null);
  const revealedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reveal = () => {
      if (revealedRef.current) return;
      const target = el;

      // Check if the target itself has any scroll-reveal class (which makes it opacity: 0).
      // If it does, we must reveal the target itself, otherwise it will remain invisible
      // and hide all of its children.
      const hasRevealClass = Array.from(target.classList).some((cls) =>
        cls.startsWith("scroll-reveal")
      );

      const children = target.querySelectorAll(selector);

      if (children.length === 0 || hasRevealClass) {
        target.classList.add(revealClass);
      }

      if (children.length > 0) {
        children.forEach((child, i) => {
          const childEl = child as HTMLElement;
          childEl.style.transitionDelay = `${i * staggerDelay}ms`;
          childEl.classList.add(revealClass);
        });
      }

      revealedRef.current = true;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal();
            if (once) {
              observer.unobserve(el);
            }
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(el);

    // Fix: immediately reveal elements that are already visible on mount.
    // IntersectionObserver sometimes misses the initial callback when the
    // element is already in the viewport at observe() time.
    const checkVisibility = () => {
      if (revealedRef.current) return;
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const isVisible = rect.top < windowHeight && rect.bottom > 0;
      if (isVisible) {
        reveal();
        if (once) {
          observer.unobserve(el);
        }
      }
    };

    // Staggered checks to handle page transitions and rendering delays
    requestAnimationFrame(checkVisibility);
    const t1 = setTimeout(checkVisibility, 50);
    const t2 = setTimeout(checkVisibility, 150);
    const t3 = setTimeout(checkVisibility, 350);
    const t4 = setTimeout(checkVisibility, 600);

    return () => {
      observer.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [threshold, rootMargin, once, selector, staggerDelay, revealClass]);

  return ref;
}

export function useScrollProgress() {
  const progress = useRef(0);
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const newProgress = docHeight > 0 ? scrollTop / docHeight : 0;
      progress.current = Math.min(Math.max(newProgress, 0), 1);

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const bar = document.getElementById("scroll-progress-bar");
        if (bar) {
          bar.style.transform = `scaleX(${progress.current})`;
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return progress;
}